import { useEffect, useMemo, useRef, useState } from "react";

import directorApi from "@contexts/director/api";
import { useDirectorContext } from "@contexts/director";
import UpdateJobStages from "../../dashboard/UpdateJobStages";

/**
 * Botão "Atualizar Diretoria" (Task 16D).
 *
 * O gate de visibilidade é o MESMO acesso que já mostra o módulo (§4.9 da spec): se a leitura de
 * prontidão (`GET /director/readiness`) devolver 403, o botão inteiro some — não existe um estado
 * "vê mas não pode clicar" nesta v1.
 *
 * Estados: idle (prontidão ok, sem aviso) / warning (`quota_warning`, ainda clicável) /
 * blocked (`!ready`, desabilitado com os motivos em pt-BR) / running (progresso via
 * `UpdateJobStages`, reaproveitado com um `fetchJob` novo) / succeeded (recarrega os dados do
 * módulo via `onSucceeded`) / failed (texto do `error_code`; `source_rate_limited` ganha um
 * horário de nova tentativa — prioridade `readiness_hint.sienge_quota_blocked_until`, depois
 * `job.retryAfterSeconds` somado a um instante-âncora fixo (o `completed_at`/`updated_at` do job,
 * ou — na ausência deles — o instante em que o job passou a existir, memoizado por `job.id`), por
 * fim texto genérico sem horário; horário sempre formatado no fuso `America/Sao_Paulo`).
 *
 * Ao montar (ou trocar de empresa) também consulta `GET /director/update` (§4.9): se já existir um
 * job não-terminal (outro usuário disparou, ou a página recarregou no meio de uma execução), o
 * progresso é retomado em vez de mostrar o botão como se nada estivesse rodando.
 */

const REASON_MESSAGES = {
  entitlement_inactive: "Contrato Diretoria não está ativo para esta empresa.",
  portfolio_missing: "Portfólio não configurado — configure no backoffice.",
  portfolio_ambiguous: "Mais de um portfólio ativo para esta empresa — corrija no backoffice.",
  portfolio_unmapped_projects: "Existem obras contratadas sem mapeamento técnico no portfólio.",
  scopes_missing: "Permissões Sienge (Parcelas a Receber/Pagar) não liberadas para esta empresa.",
  scopes_stale: "Permissões Sienge não foram verificadas nos últimos 7 dias.",
  quota_insufficient: "Cota diária da API Sienge insuficiente para esta atualização.",
  director_mode_disabled: "Atualização da Diretoria está temporariamente desativada.",
};

// Códigos que o ciclo Diretoria (`run_director_cycle`) pode devolver como `error_code` de um job
// `failed` (ver Task 16 header do plano, §4.6/§6 da spec). `source_rate_limited` tem tratamento
// próprio (mensagem de horário de nova tentativa) — os demais só precisam de um texto pt-BR em vez
// do genérico "(código)" que `UpdateJobStages` já mostra.
const FAILED_JOB_CODE_MESSAGES = {
  active_portfolio_ambiguous: "Mais de um portfólio ativo foi encontrado durante a execução — corrija no backoffice.",
  director_source_error: "Erro ao buscar dados na fonte (Sienge) durante a execução.",
  director_publish_blocked: "A publicação dos dados foi bloqueada — o ciclo não pôde ser concluído.",
  director_mode_disabled: "Atualização da Diretoria está temporariamente desativada (modo de emergência).",
  director_cycle_in_progress: "Já existe uma atualização da Diretoria em andamento.",
  // `service_restarted` NÃO entra aqui: o `UpdateJobStages` compartilhado já renderiza o texto
  // específico desse código (PR #148); repetir aqui duplicava a mensagem na tela.
};

// Motivos de `blocked` que NÃO são erro (fix round 2, contrato AMENDMENT 2): a Diretoria já foi
// atualizada hoje é esperado, não uma falha — passado a `UpdateJobStages` para render em tom
// neutro (`update-stages-note`) em vez do vermelho padrão de bloqueio/erro.
const BLOCKED_JOB_NOTICE_MESSAGES = {
  director_cycle_already_completed_today:
    "A Diretoria já foi atualizada hoje. Nova atualização disponível amanhã.",
};

const reasonMessage = (reason, readiness) => {
  if (reason === "portfolio_unmapped_projects") {
    const names = (readiness?.unmapped_contracted_projects || [])
      .map((project) => project?.name)
      .filter(Boolean);
    if (names.length) return `Obras contratadas sem mapeamento técnico no portfólio: ${names.join(", ")}.`;
  }
  if (reason === "scopes_missing") {
    const missing = readiness?.missing_scopes || [];
    if (missing.length) return `Permissões Sienge não liberadas: ${missing.join(", ")}.`;
  }
  return REASON_MESSAGES[reason] || "Não foi possível confirmar a prontidão desta atualização.";
};

const TERMINAL_STATUSES = ["succeeded", "failed", "blocked"];

// Fix round 3: o horário de nova tentativa é sempre o do fuso do Sienge/da regra de negócio
// (América/São Paulo, ver plan.md — "próximo 00:00 em America/Sao_Paulo"), não o do relógio de
// quem está vendo a tela nem do runner que roda os testes. Sem `timeZone` explícito,
// `toLocaleTimeString`/`toLocaleDateString` usam o fuso da máquina — em CI (UTC) o texto sairia
// errado, e em produção dependeria do fuso do navegador do usuário.
const RETRY_TIME_ZONE = "America/Sao_Paulo";

const formatRetryTime = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const time = date.toLocaleTimeString("pt-BR", { timeZone: RETRY_TIME_ZONE, hour: "2-digit", minute: "2-digit" });
  const day = date.toLocaleDateString("pt-BR", { timeZone: RETRY_TIME_ZONE, day: "2-digit", month: "2-digit" });
  return `${time} de ${day}`;
};

const DirectorUpdateButton = ({ onSucceeded, api = directorApi }) => {
  const { selectedCompanyId } = useDirectorContext();
  const [hidden, setHidden] = useState(false);
  const [readiness, setReadiness] = useState(null);
  const [readinessError, setReadinessError] = useState(null);
  const [readinessLoading, setReadinessLoading] = useState(false);
  const [job, setJob] = useState(null);
  const [triggering, setTriggering] = useState(false);
  const [triggerMessage, setTriggerMessage] = useState(null);
  const [readinessHint, setReadinessHint] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => () => { mountedRef.current = false; }, []);

  const loadReadiness = async (companyId) => {
    setReadinessLoading(true);
    setReadinessError(null);
    try {
      const data = await api.fetchDirectorReadiness(companyId);
      if (!mountedRef.current) return;
      setReadiness(data);
      setHidden(false);
    } catch (error) {
      if (!mountedRef.current) return;
      if (error?.status === 403) {
        setHidden(true);
        setReadiness(null);
      } else {
        setReadinessError(error);
      }
    } finally {
      if (mountedRef.current) setReadinessLoading(false);
    }
  };

  // Retoma um job não-terminal já em curso (outro usuário disparou a atualização, ou a página
  // recarregou no meio de uma execução) — sem isso o botão mostraria "Atualizar Diretoria" como se
  // nada estivesse rodando enquanto o pipeline segue no ar.
  const resumeJob = async (companyId) => {
    let result;
    try {
      result = await api.fetchDirectorUpdateJob(companyId);
    } catch {
      return;
    }
    if (!mountedRef.current) return;
    if (!result?.ok) return;
    if (result.body?.readiness_hint) setReadinessHint(result.body.readiness_hint);
    const fetchedJob = result.body?.job ?? null;
    if (fetchedJob && !TERMINAL_STATUSES.includes(fetchedJob.status)) setJob(fetchedJob);
  };

  useEffect(() => {
    if (!selectedCompanyId) return;
    setJob(null);
    setTriggerMessage(null);
    setReadinessHint(null);
    loadReadiness(selectedCompanyId);
    resumeJob(selectedCompanyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId]);

  // Fix round 3: `job.retryAfterSeconds` é um intervalo relativo ("espere N segundos"), não um
  // instante — sem um ponto fixo para somar, calcular a partir de `Date.now()` a cada render faz o
  // horário exibido avançar a cada poll (o "tente novamente às HH:MM" muda sozinho na tela do
  // usuário). Ancora em `completed_at`/`updated_at` do job quando o BE os fornecer (o instante real
  // em que o job terminou); na ausência deles, memoiza o instante calculado por `job.id` +
  // `retryAfterSeconds` para que ele fique fixo durante toda a vida deste job, mesmo com re-renders.
  const retryAnchorMs = useMemo(() => {
    const anchorSource = job?.completed_at ?? job?.completedAt ?? job?.updated_at ?? job?.updatedAt;
    if (anchorSource) {
      const anchorDate = anchorSource instanceof Date ? anchorSource : new Date(anchorSource);
      if (!Number.isNaN(anchorDate.getTime())) return anchorDate.getTime();
    }
    return Date.now();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id, job?.retryAfterSeconds]);

  if (!selectedCompanyId || hidden) return null;

  const jobActive = !!job && !TERMINAL_STATUSES.includes(job.status);
  const ready = readiness?.ready === true;
  const reasons = readiness?.reasons ?? [];
  const missingScopes = readiness?.missing_scopes ?? [];
  const quotaWarning = !!readiness?.quota_warning;

  const handleClick = async () => {
    if (triggering || jobActive) return;
    setTriggering(true);
    setTriggerMessage(null);
    const result = await api.triggerDirectorUpdate(selectedCompanyId);
    if (!mountedRef.current) return;
    setTriggering(false);
    if (result.status === 403) {
      setHidden(true);
      return;
    }
    if (result.status === 202 && result.body?.job) {
      setJob(result.body.job);
      return;
    }
    if (result.status === 409) {
      setTriggerMessage({ type: "conflict", text: "Atualização já em andamento." });
      if (result.body?.job) setJob(result.body.job);
      return;
    }
    if (result.status === 422) {
      setTriggerMessage({
        type: "blocked",
        text: "A atualização não pode começar agora — veja os motivos abaixo.",
      });
      if (result.body?.readiness) {
        setReadiness(result.body.readiness);
      } else if (Array.isArray(result.body?.reasons)) {
        // O BE pode devolver só `reasons` sem o payload de prontidão inteiro — mescla no estado
        // atual em vez de perder o resto do checklist (portfólio/escopos/cota já carregados).
        setReadiness((previous) => ({ ...(previous || {}), ready: false, reasons: result.body.reasons }));
      }
      return;
    }
    setTriggerMessage({
      type: "error",
      text: result.body?.message || "Não foi possível iniciar a atualização. Tente novamente.",
    });
  };

  const fetchJob = async () => {
    const result = await api.fetchDirectorUpdateJob(selectedCompanyId);
    if (!mountedRef.current) return { ok: false, message: "Não foi possível consultar o andamento." };
    if (!result.ok) return { ok: false, message: "Não foi possível consultar o andamento." };
    if (result.body?.readiness_hint) setReadinessHint(result.body.readiness_hint);
    return { ok: true, job: result.body?.job ?? null };
  };

  const handleTerminal = (terminalJob) => {
    if (!mountedRef.current) return;
    setJob(terminalJob);
    if (terminalJob?.status === "succeeded") {
      onSucceeded?.();
      loadReadiness(selectedCompanyId);
    }
  };

  // Prioridade do texto de nova tentativa (contrato AMENDMENT 2): `readiness_hint` é a fonte mais
  // fresca (vem de `GET /director/update`, lida a cada poll) — só cai para `retryAfterSeconds` do
  // próprio job (calculado a partir de "agora") quando o hint ainda não chegou, e para o texto
  // genérico sem horário quando nenhum dos dois está disponível.
  const retryAfter = job?.status === "failed" && job?.error_code === "source_rate_limited"
    ? (() => {
        const hintFormatted = formatRetryTime(readinessHint?.sienge_quota_blocked_until);
        if (hintFormatted) return `Limite diário do Sienge atingido. Tente novamente após ${hintFormatted}.`;
        const retrySeconds = job?.retryAfterSeconds;
        if (Number.isInteger(retrySeconds) && retrySeconds >= 0) {
          const computedFormatted = formatRetryTime(new Date(retryAnchorMs + retrySeconds * 1000));
          if (computedFormatted) return `Limite diário do Sienge atingido. Tente novamente após ${computedFormatted}.`;
        }
        return "Limite diário do Sienge atingido. Tente novamente mais tarde.";
      })()
    : null;
  const failedDetail = job?.status === "failed" && !retryAfter
    ? FAILED_JOB_CODE_MESSAGES[job?.error_code]
    : null;

  return (
    <section className="director-update">
      <button
        type="button"
        className="director-button"
        onClick={handleClick}
        disabled={triggering || jobActive || readinessLoading || !ready}
      >
        {jobActive ? "Atualizando…" : "Atualizar Diretoria"}
      </button>

      {readinessError && (
        <p className="director-state director-state--error" role="alert">
          {readinessError.message || "Não foi possível carregar a prontidão desta atualização."}
        </p>
      )}

      {readiness && (
        <ul className="director-update__checklist">
          <li data-status={readiness.portfolio_configured ? "ok" : "fail"}>Portfólio</li>
          <li data-status={readiness.scopes_ok ? "ok" : "fail"}>
            Permissões Sienge
            {!readiness.scopes_ok && missingScopes.length > 0 && (
              <small className="director-update__missing-scopes">
                {` (faltando: ${missingScopes.join(", ")})`}
              </small>
            )}
          </li>
          <li data-status={readiness.quota_ok ? (quotaWarning ? "warning" : "ok") : "fail"}>Cota</li>
        </ul>
      )}

      {readiness && !ready && (
        <p className="director-update__blocked">
          {reasons.map((reason) => reasonMessage(reason, readiness)).join(" ")}
        </p>
      )}

      {readiness && ready && quotaWarning && (
        <p className="director-update__warning">
          {`Esta atualização vai consumir ${readiness.expected_request_count} de `
            + `${readiness.remaining_quota_today} chamadas restantes hoje.`}
        </p>
      )}

      {triggerMessage && (
        <p
          className={`director-update__${triggerMessage.type}`}
          role={triggerMessage.type === "error" ? "alert" : "status"}
        >
          {triggerMessage.text}
        </p>
      )}

      {job && (
        <UpdateJobStages
          key={job.id}
          initialJob={job}
          fetchJob={fetchJob}
          onTerminal={handleTerminal}
          quotaBlockedUntil={readinessHint?.sienge_quota_blocked_until ?? null}
          blockedNotices={BLOCKED_JOB_NOTICE_MESSAGES}
        />
      )}

      {retryAfter && (
        <p className="director-update__retry">{retryAfter}</p>
      )}
      {failedDetail && (
        <p className="director-update__failed-detail">{failedDetail}</p>
      )}
    </section>
  );
};

export default DirectorUpdateButton;
